import OptionalChip from "@/components/Common/forms/OptionalChip";
import SkeletonLoader from "@/components/onboarding/SkeletonLoader";
import clsxm from "@/lib/clsxm";
import {
  useEffect,
  useId,
  useRef,
  type ClipboardEvent,
  type FC,
  type HTMLInputTypeAttribute,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
} from "react";
import {useTranslation} from "react-i18next";

const InputField: FC<{
  ref?: RefObject<HTMLInputElement | null>;
  autoFocus?: boolean;
  onChange?: (value: string) => void;
  onBlur?: (value: string) => void;
  error?: string | null;
  errorClassName?: string;
  icon?: ReactNode;
  type?: HTMLInputTypeAttribute;
  label?: string | ReactNode;
  value?: string | null;
  placeholder?: string;
  className?: string;
  onClick?: () => void;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  onPaste?: (e: ClipboardEvent<HTMLInputElement>) => void;
  otherIcon?: ReactNode;
  isDisabled?: boolean;
  isLoading?: boolean;
  isReadonly?: boolean;
  labelClassName?: string;
  containerClassName?: string;
  name?: string;
  customFieldId?: string;
  dataTestId?: string;
  maxLength?: number;
  optional?: boolean;
}> = ({
  ref,
  autoFocus,
  onChange,
  onBlur,
  error = null,
  errorClassName,
  icon = null,
  label,
  placeholder,
  type = "text",
  value,
  className,
  onClick,
  onKeyDown,
  onPaste,
  otherIcon = null,
  isDisabled,
  isLoading,
  isReadonly = false,
  labelClassName,
  containerClassName,
  name,
  customFieldId,
  dataTestId,
  maxLength,
  optional = false,
}) => {
  const {t} = useTranslation("common");
  const fieldId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const isInputFieldDisabled = isDisabled || !onChange;

  useEffect(() => {
    if (inputRef.current && error) {
      inputRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [error]);

  return (
    <div className={clsxm("flex w-full flex-col gap-1", containerClassName)}>
      {!!label && (
        <label
          htmlFor={customFieldId || fieldId}
          className={clsxm("text-neutral-grey-dark flex min-h-5 gap-2 text-sm font-medium", labelClassName)}>
          {optional && <OptionalChip />}
          {label}
        </label>
      )}
      <div className="relative" onClick={() => !isInputFieldDisabled && onClick && onClick()} ref={ref}>
        {isLoading ? (
          <SkeletonLoader className={clsxm("border-neutral-grey-light h-14 rounded-md border", className)} />
        ) : (
          <input
            readOnly={isReadonly}
            disabled={isInputFieldDisabled}
            id={customFieldId || fieldId}
            data-testid={dataTestId}
            className={clsxm(
              "border-neutral-grey-light text-neutral-black placeholder:text-neutral-grey-light focus:border-primary-medium-solid read-only:enabled:hover:bg-neutral-white-dark disabled:bg-neutral-white-dark disabled:text-neutral-grey-dark flex h-14 w-full rounded-md border bg-white p-4 outline-hidden transition-all duration-150 read-only:enabled:cursor-pointer disabled:cursor-not-allowed",
              error && "border-danger-medium-red focus:border-danger-dark-red",
              className,
            )}
            type={type}
            placeholder={placeholder}
            maxLength={maxLength}
            {...(typeof value !== "undefined" && value !== null && {value})}
            ref={inputRef}
            name={name}
            onChange={({target: {value}}) => onChange?.(value)}
            onBlur={({target: {value}}) => onBlur?.(value)}
            onKeyDown={onKeyDown}
            onPaste={onPaste}
            autoFocus={autoFocus}
          />
        )}
        {icon}
        {otherIcon}
      </div>
      {!!error && (
        <span className={clsxm("text-danger-medium-red mt-0.5 text-xs", errorClassName)}>
          {t(error as "an_error_occurred")}
        </span>
      )}
    </div>
  );
};

export default InputField;
